import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Send, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestInterface() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const startTime = useRef(Date.now());

  useEffect(() => {
    axios.get(`${API}/tests/${testId}`).then(r => {
      setTest(r.data);
      setTimeLeft(r.data.time_limit_minutes * 60);
      setLoading(false);
    }).catch(() => { toast.error("Failed to load test"); navigate("/"); });
  }, [testId, navigate]);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0 || !test) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeLeft === 0 && test) {
      toast.warning("Time's up! Auto-submitting...");
      submitTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const selectAnswer = (qId, option) => {
    setAnswers(prev => ({
      ...prev,
      [qId]: { ...prev[qId], selected: prev[qId]?.selected === option ? null : option, confidence: prev[qId]?.confidence || "sure" }
    }));
  };

  const toggleConfidence = (qId) => {
    setAnswers(prev => ({
      ...prev,
      [qId]: { ...prev[qId], confidence: prev[qId]?.confidence === "sure" ? "guessed" : "sure" }
    }));
  };

  const submitTest = async () => {
    setSubmitting(true);
    try {
      const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
      await axios.post(`${API}/tests/${testId}/submit`, { answers, time_taken_seconds: timeTaken });
      toast.success("Test submitted successfully!");
      navigate(`/test/${testId}/results`);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading || !test) {
    return <div className="h-screen flex items-center justify-center"><div className="text-muted-foreground">Loading test...</div></div>;
  }

  const questions = test.questions || [];
  const q = questions[currentQ];
  const answered = Object.keys(answers).filter(k => answers[k]?.selected).length;
  const timerClass = timeLeft < 300 ? "timer-critical" : timeLeft < 600 ? "timer-warning" : "";

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="test-interface">
      {/* Header */}
      <header className="h-14 border-b border-border flex items-center justify-between px-6 bg-card shrink-0">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="font-mono text-xs">{test.test_code}</Badge>
          <Badge variant="outline" className="font-mono text-xs">Booklet {test.booklet_code}</Badge>
        </div>
        <div className={`timer-display text-2xl font-bold ${timerClass}`} data-testid="exam-timer">
          {formatTime(timeLeft)}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {answered}/{questions.length} answered
          </span>
          <Button
            data-testid="submit-test-btn"
            size="sm"
            onClick={() => setShowSubmitDialog(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Send size={14} className="mr-1" /> Submit
          </Button>
        </div>
      </header>

      {/* Main Area */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        {/* Question Panel */}
        <div className="lg:col-span-9 overflow-y-auto p-6 lg:p-8">
          <div className="max-w-3xl mx-auto">
            {/* Question Number & Confidence */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-muted-foreground">Q.{currentQ + 1} of {questions.length}</span>
                <Badge variant="outline" className="text-xs capitalize">{q?.difficulty_level || "medium"}</Badge>
                {q?.question_type === "pair_based" && (
                  <Badge className="bg-amber-500/20 text-amber-400 text-xs border-0">Statement Based</Badge>
                )}
                {q?.is_year_specific_ca && (
                  <Badge className="bg-orange-500/20 text-orange-400 text-xs border-0" data-testid="ca-badge">
                    Year-Specific CA
                  </Badge>
                )}
              </div>
              {answers[q?.id]?.selected && (
                <div className="flex items-center gap-2 confidence-toggle" data-testid="confidence-toggle">
                  <span className="text-xs text-muted-foreground">
                    {answers[q?.id]?.confidence === "sure" ? (
                      <span className="flex items-center gap-1"><Eye size={14} className="text-emerald-400" /> Sure</span>
                    ) : (
                      <span className="flex items-center gap-1"><EyeOff size={14} className="text-amber-400" /> Guessed</span>
                    )}
                  </span>
                  <Switch
                    data-testid="confidence-switch"
                    checked={answers[q?.id]?.confidence === "sure"}
                    onCheckedChange={() => toggleConfidence(q?.id)}
                  />
                </div>
              )}
            </div>

            {/* Question Text */}
            <div className="mb-6">
              <p className="text-base leading-relaxed whitespace-pre-line" data-testid="question-text">
                {q?.question_text}
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {q?.options && Object.entries(q.options).map(([key, text]) => {
                const isSelected = answers[q.id]?.selected === key;
                return (
                  <div
                    key={key}
                    data-testid={`option-${key}`}
                    className={`question-option flex items-start gap-3 ${isSelected ? "selected" : ""}`}
                    onClick={() => selectAnswer(q.id, key)}
                  >
                    <div className={`w-8 h-8 rounded-sm border flex items-center justify-center shrink-0 font-mono text-sm transition-colors ${
                      isSelected ? "bg-primary border-primary text-white" : "border-border"
                    }`}>
                      {key}
                    </div>
                    <span className="text-sm leading-relaxed pt-1">{text}</span>
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button
                data-testid="prev-question-btn"
                variant="outline"
                size="sm"
                disabled={currentQ === 0}
                onClick={() => setCurrentQ(prev => prev - 1)}
              >
                <ChevronLeft size={16} /> Previous
              </Button>
              <Button
                data-testid="next-question-btn"
                variant="outline"
                size="sm"
                disabled={currentQ === questions.length - 1}
                onClick={() => setCurrentQ(prev => prev + 1)}
              >
                Next <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation Panel */}
        <div className="lg:col-span-3 border-l border-border bg-card p-4 overflow-y-auto">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Question Navigator</p>
          <div className="question-nav-grid" data-testid="question-navigator">
            {questions.map((item, i) => {
              const isAnswered = answers[item.id]?.selected;
              const isCurrent = i === currentQ;
              return (
                <button
                  key={i}
                  data-testid={`nav-q-${i+1}`}
                  className={`question-nav-btn ${isCurrent ? "current" : ""} ${isAnswered ? "answered" : ""} ${item.is_year_specific_ca ? "!border-orange-500/50 !bg-orange-500/10" : ""}`}
                  onClick={() => setCurrentQ(i)}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm bg-primary" /> <span>Current</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm bg-primary/20 border border-primary/50" /> <span>Answered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm border border-border" /> <span>Not Answered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm bg-orange-500/20 border border-orange-500/50" /> <span>Year CA</span>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-400" /> Confirm Submission
            </DialogTitle>
            <DialogDescription>
              You have answered <strong className="text-foreground">{answered}</strong> out of <strong className="text-foreground">{questions.length}</strong> questions.
              {questions.length - answered > 0 && (
                <span className="text-amber-400 block mt-1">
                  {questions.length - answered} questions are unattempted.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="cancel-submit-btn" variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Go Back
            </Button>
            <Button
              data-testid="confirm-submit-btn"
              disabled={submitting}
              onClick={submitTest}
              className="bg-primary hover:bg-primary/90"
            >
              {submitting ? "Submitting..." : "Confirm Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
